import React, {useEffect, useState} from 'react'
import {Container} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import { resetConfirmationDialog, toggleConfirmationDialog, toggleLoader } from '../../shared/actions/setting';
import {deleteUserPrivilege, exportPrivilegesYml, getPrivileges } from '../../utils/mainServices';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import PrivilegePopup from './PrivilegePopup';
import HierarchicalPrivilegeTable from './HierarchicalPrivilegeTable';
import SearchInput from '../../shared/components/SearchInput';

const PrivilagePageTable = () => {
  const dispatch = useDispatch();
  const [allPrivilegeList, setAllPrivilegeList] = useState([]);
  const [filteredPrivilageList, setFilteredPrivilegeList] = useState([])
  const {t} = useTranslation();
  const [ deletePrivilegeId, setDeletePrivilegeId] = useState(null);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [expandedParentIds, setExpandedParentIds] = useState([]);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupType, setPopupType] = useState('add-high');
  const [selectedPrivilegeForEdit, setSelectedPrivilegeForEdit] = useState(null);
  const [parentPrivilegeId, setParentPrivilegeId] = useState(null);
  const [searchText, setSearchText] = useState('');

  const handlePrivilegeSearch = (input) => {
    const raw = typeof input === 'string' ? input : (input?.target?.value ?? "");
    setSearchText(raw);

    const val = raw.toString().toLowerCase().trim();
    if (!val) {
      setFilteredPrivilegeList(allPrivilegeList);
      setExpandedParentIds([]);
      return;
    }

    const filtered = allPrivilegeList.reduce((acc, parent) => {
      const parentMatch = parent.name?.toString().toLowerCase().includes(val);
      const children = Array.isArray(parent.child) ? parent.child : [];
      const matchingChildren = children.filter(ch =>
        ch.name?.toString().toLowerCase().includes(val)
      );

      if (parentMatch) acc.push(parent);
      else if (matchingChildren.length > 0) acc.push({ ...parent, child: matchingChildren });

      return acc;
    }, []);

    const expandIds = filtered
      .filter(p => Array.isArray(p.child) && p.child.length > 0 && !p.name?.toString().toLowerCase().includes(val))
      .map(p => p.id);
    setExpandedParentIds(expandIds);
    setFilteredPrivilegeList(filtered);
  };

  const confirmationDialog = useSelector(state => {
    return state.setting.confirmationDialog
  });

  const handleAddHighPrivilege = () => {
    setPopupType('add-high');
    setSelectedPrivilegeForEdit(null);
    setParentPrivilegeId(null);
    setIsPopupOpen(true);
  };

  const handleAddSubPrivilege = (parentId) => {
    setPopupType('add-new');
    setSelectedPrivilegeForEdit(null);
    setParentPrivilegeId(parentId);
    setIsPopupOpen(true);
  };

  const handleEditPrivilege = (privilege) => {
    setPopupType('update');
    setSelectedPrivilegeForEdit(privilege);
    setParentPrivilegeId(privilege.parentId);
    setIsPopupOpen(true);
  };

  const handlePopupSave = async (formData) => {
    setShouldFetchData(prev => !prev);
  };

  const handleExportPrivilegesYml = async () => {
    dispatch(toggleLoader(true));
    try {
      const response = await exportPrivilegesYml();
      const contentDisposition = response.headers['content-disposition'];
      const fileNameMatch = contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/);
      const fileName = fileNameMatch ? fileNameMatch[1] : 'privileges.yml';
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/x-yaml' }));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Privileges yml exported successfully');
    } catch (error) {
      toast.error("Privileges yml can't be exported");
    } finally {
      dispatch(toggleLoader(false));
    }
  };

  //Fetch the all privileges
  useEffect(() => {
    const fetchPrivileges = async () => {
      dispatch(toggleLoader(true))
      try {
        const data = await getPrivileges();
        if (data.message === "Success") {
        setAllPrivilegeList(data.content);
        setFilteredPrivilegeList(data.content);
        setExpandedParentIds([]);
        } else {
          toast.error("Privileges can't be fetched");
        }
      } catch (error) {
        console.error('Error fetching privileges:', error);
        let beMessage = error?.response?.data?.message;
        if (!beMessage) {
          if (error.message) beMessage = error.message;
          else beMessage = "Privileges can't be fetched";
        }
        toast.error(beMessage);
      } finally {
        dispatch(toggleLoader(false))
      }
    };

    fetchPrivileges();
  }, [shouldFetchData, dispatch]); 

  function handleDeletePrivilege(id, privilege) {
    if (privilege && privilege.child && privilege.child.length > 0) {
      toast.warn("Cannot delete a privilege that has sub-privileges. Please delete the sub-privileges first.");
      return;
    }
    dispatch(toggleConfirmationDialog({
      isVisible: true,
      confirmationHeading: t("confirmation_msg.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_PRIVILEGE_WITH_NAME", { name: privilege?.name || '' }),
      confirmationDescription: t("confirmation_msg.THE_DELETE_ACTION_WILL_REMOVE_THE_PRIVILEGE_WITH_NAME", { name: privilege?.name || '' })
    }));
    setDeletePrivilegeId(id);
  }

  useEffect(() => {
    if (!confirmationDialog || !confirmationDialog.onSuccess || !deletePrivilegeId) {
      return;
    }
  
    const handleDelete = async () => {
      try {
        dispatch(toggleLoader(true));
        const result = await deleteUserPrivilege(deletePrivilegeId);
        if (result && result.message === "Success") {
        toast.success("Privilege deleted successfully");
        setShouldFetchData(prev => !prev);
        } else {
          toast.error(result?.message || "Failed to delete privilege");
        }
      } catch (error) {
        console.error('Error deleting privilege:', error);
        let beMessage = error?.response?.data?.message;
        if (!beMessage) {
          if (error.message) beMessage = error.message;
          else beMessage = "Failed to delete privilege";
        }
        toast.error(beMessage);
      } finally {
        dispatch(toggleLoader(false));
        setDeletePrivilegeId(null);
        dispatch(resetConfirmationDialog());
      }
    };
  
    handleDelete();
  }, [confirmationDialog, deletePrivilegeId, dispatch]);

  return (
    <div className={"half-list-container"}>
      <Container>
        <div className={"single-card m-rl-m-8 p-a-16"}>
          <div className={"sa-table-flex sa-table-top"}>
            <span className="sa-table-top-left-items">
              <SearchInput
                value={searchText}
                onValueChange={handlePrivilegeSearch}
                placeholder="Search Privilege"
              />
            </span>
            <div className="sa-table-top-right-items">
              <span>
                <div className="xin-pull-right">
                  <span className="sa-table-icon-flex sa-table-filter-position">
                   <button
                      className={"sa-table-btn-secondary sa-table-btn-secondary-wide sa-table-float-right"}
                       onClick={handleExportPrivilegesYml}
                   >
                    <FeatherIcon
                        icon="download"
                        className="sa-table-icon-size"
                    />
                    Export Yml
                  </button>
                   <button
                      className={"sa-table-btn-secondary sa-table-btn-secondary-wide sa-table-float-right"}
                       onClick={handleAddHighPrivilege}
                   >
                    <FeatherIcon
                        icon="plus"
                        className="sa-table-icon-size"
                    />
                    Add High Privilege
                  </button>
                  </span>
                </div>
              </span>
            </div>
          </div>
          <div id="main3" className="sa-table-container xindicate-calc-height">
            <table className="table table-borderless sa-table-width hier-priv">
              <thead>
                <tr>
                  <th className={"sa-table-head-sticky"} style={{width: "30%"}}>Privileges</th>
                  <th className={"sa-table-head-sticky"} style={{width: "40%"}}>Actions</th>
                  <th className={"sa-table-head-sticky"} style={{width: "20%"}}>Description</th>
                  <th className={"sa-table-head-sticky"} style={{width: "10%"}}>Action</th>
                </tr>
              </thead>
              <tbody>
                <HierarchicalPrivilegeTable
                  privileges={filteredPrivilageList}
                  onEditPrivilege={handleEditPrivilege}
                  onDeletePrivilege={handleDeletePrivilege}
                  onAddSubPrivilege={handleAddSubPrivilege}
                  mode="privilege"
                  showActions={true}
                  showExpandCollapse={true}
                  defaultExpandedIds={expandedParentIds}
                />
              </tbody>
            </table>
            {filteredPrivilageList && filteredPrivilageList.length === 0 && (
              <div className={"empty-results mt-5"}>
                <FeatherIcon icon="info"/>
                <div className={"empty-results-text"}>{t("emptyMsg.THERE_ARE_NO_PRIVILEGES")}</div>
              </div>
            )}
          </div>
        </div>
      </Container>

        <PrivilegePopup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            onSave={handlePopupSave}
            type={popupType}
            defaultValues={selectedPrivilegeForEdit}
            existingPrivileges={allPrivilegeList}
            parentId={parentPrivilegeId}
        />


    </div>
  );
};

export default PrivilagePageTable;
